import { useState, useEffect } from "react";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import MenuItem from "@mui/material/MenuItem";
import Chip from "@mui/material/Chip";
import axios from "axios";
import API_ENDPOINTS from "config/api";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DataTable from "examples/Tables/DataTable";

export default function Movimientos() {
  const [movimientos, setMovimientos] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [productos, setProductos] = useState([]);
  const [almacenes, setAlmacenes] = useState([]);
  const [filtroTipo, setFiltroTipo] = useState("todos");
  const [fechaInicio, setFechaInicio] = useState("");
  const [fechaFin, setFechaFin] = useState("");
  const [movimientoForm, setMovimientoForm] = useState({
    producto_id: "",
    almacen_id: "",
    tipo_movimiento: "entrada",
    cantidad: "",
    motivo: "",
    referencia: "",
    observaciones: "",
  });

  // const endpoint = ... (removed)
  const empresaId = localStorage.getItem("empresaId");

  const tiposMovimiento = [
    { value: "entrada", label: "Entrada", color: "success" },
    { value: "salida", label: "Salida", color: "error" },
    { value: "ajuste", label: "Ajuste", color: "warning" },
    { value: "transferencia", label: "Transferencia", color: "info" },
  ];

  useEffect(() => {
    fetchMovimientos();
    fetchProductos();
    fetchAlmacenes();
  }, [empresaId, filtroTipo, fechaInicio, fechaFin]);

  const fetchMovimientos = async () => {
    try {
      const params = {
        empresa_id: empresaId,
        tipo: filtroTipo !== "todos" ? filtroTipo : undefined,
        fecha_inicio: fechaInicio || undefined,
        fecha_fin: fechaFin || undefined,
      };
      const response = await axios.get(API_ENDPOINTS.MOVIMIENTOS, { params });
      setMovimientos(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error al obtener movimientos:", error);
      setMovimientos([]);
    }
  };

  const fetchProductos = async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.INVENTARIO.GENERAL, {
        params: { empresa_id: empresaId },
      });
      setProductos(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error al obtener productos:", error);
      setProductos([]);
    }
  };

  const fetchAlmacenes = async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.ALMACENES, {
        params: { empresa_id: empresaId },
      });
      setAlmacenes(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error al obtener almacenes:", error);
      setAlmacenes([]);
    }
  };

  const handleOpenDialog = () => {
    setMovimientoForm({
      producto_id: "",
      almacen_id: "",
      tipo_movimiento: "entrada",
      cantidad: "",
      motivo: "",
      referencia: "",
      observaciones: "",
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setMovimientoForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    if (!movimientoForm.producto_id || !movimientoForm.almacen_id || !movimientoForm.cantidad) {
      alert("Por favor complete los campos obligatorios: Producto, Almacén y Cantidad");
      return;
    }

    setLoading(true);
    try {
      const data = {
        ...movimientoForm,
        empresa_id: localStorage.getItem("empresaId") || 1,
        producto_id: parseInt(movimientoForm.producto_id),
        almacen_id: parseInt(movimientoForm.almacen_id),
        cantidad: parseFloat(movimientoForm.cantidad),
      };

      await axios.post(API_ENDPOINTS.MOVIMIENTOS, data);
      alert("Movimiento registrado exitosamente");
      fetchMovimientos();
      handleCloseDialog();
    } catch (error) {
      console.error("Error al registrar movimiento:", error);
      const errorMsg = error.response?.data?.message || error.message;
      alert("Error al registrar el movimiento: " + errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const getTipoChip = (tipo) => {
    const tipoObj = tiposMovimiento.find((t) => t.value === tipo);
    return <Chip label={tipoObj?.label || tipo} color={tipoObj?.color || "default"} size="small" />;
  };

  const columns = [
    { Header: "Fecha", accessor: "fecha", width: "12%", align: "left" },
    { Header: "Tipo", accessor: "tipo", width: "12%", align: "center" },
    { Header: "Producto", accessor: "producto", align: "left" },
    { Header: "Almacén", accessor: "almacen", width: "15%", align: "left" },
    { Header: "Cantidad", accessor: "cantidad", width: "10%", align: "center" },
    { Header: "Motivo", accessor: "motivo", width: "15%", align: "left" },
    { Header: "Usuario", accessor: "usuario", width: "12%", align: "left" },
    { Header: "Acciones", accessor: "acciones", width: "8%", align: "center" },
  ];

  const rows = movimientos.map((mov) => ({
    fecha: new Date(mov.fecha).toLocaleDateString(),
    tipo: getTipoChip(mov.tipo_movimiento),
    producto: mov.producto?.nombre || "N/A",
    almacen: mov.almacen?.nombre || "N/A",
    cantidad: `${mov.cantidad} ${mov.producto?.unidad_medida || ""}`,
    motivo: mov.motivo,
    usuario: mov.usuario?.name || "Sistema",
    acciones: (
      <IconButton size="small" color="info">
        <Icon>visibility</Icon>
      </IconButton>
    ),
  }));

  return (
    <MDBox>
      <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <MDTypography variant="h6">Movimientos de Inventario</MDTypography>
        <MDButton
          variant="gradient"
          color="success"
          startIcon={<Icon>add</Icon>}
          onClick={handleOpenDialog}
        >
          Registrar Movimiento
        </MDButton>
      </MDBox>

      {/* Filtros */}
      <Card sx={{ mb: 3 }}>
        <MDBox p={3}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                select
                label="Tipo de Movimiento"
                value={filtroTipo}
                onChange={(e) => setFiltroTipo(e.target.value)}
              >
                <MenuItem value="todos">Todos</MenuItem>
                {tiposMovimiento.map((tipo) => (
                  <MenuItem key={tipo.value} value={tipo.value}>
                    {tipo.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                type="date"
                label="Fecha Inicio"
                value={fechaInicio}
                onChange={(e) => setFechaInicio(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                type="date"
                label="Fecha Fin"
                value={fechaFin}
                onChange={(e) => setFechaFin(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <MDButton variant="outlined" color="info" fullWidth onClick={fetchMovimientos}>
                Aplicar Filtros
              </MDButton>
            </Grid>
          </Grid>
        </MDBox>
      </Card>

      {/* Tabla de movimientos */}
      <Card>
        <MDBox>
          <DataTable
            table={{ columns, rows }}
            isSorted={false}
            entriesPerPage={false}
            showTotalEntries={false}
            noEndBorder
          />
        </MDBox>
      </Card>

      {/* Dialog para registrar movimiento */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>Registrar Movimiento de Inventario</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Producto"
                name="producto_id"
                value={movimientoForm.producto_id}
                onChange={handleInputChange}
                required
              >
                {productos.map((prod) => (
                  <MenuItem key={prod.id} value={prod.id}>
                    {prod.nombre} - Stock: {prod.stock_actual}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Almacén"
                name="almacen_id"
                value={movimientoForm.almacen_id}
                onChange={handleInputChange}
                required
              >
                {almacenes.map((alm) => (
                  <MenuItem key={alm.id} value={alm.id}>
                    {alm.nombre}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Tipo de Movimiento"
                name="tipo_movimiento"
                value={movimientoForm.tipo_movimiento}
                onChange={handleInputChange}
                required
              >
                {tiposMovimiento.map((tipo) => (
                  <MenuItem key={tipo.value} value={tipo.value}>
                    {tipo.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Cantidad"
                name="cantidad"
                type="number"
                value={movimientoForm.cantidad}
                onChange={handleInputChange}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Motivo"
                name="motivo"
                value={movimientoForm.motivo}
                onChange={handleInputChange}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Referencia (Opcional)"
                name="referencia"
                value={movimientoForm.referencia}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Observaciones"
                name="observaciones"
                value={movimientoForm.observaciones}
                onChange={handleInputChange}
                multiline
                rows={3}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={loading}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="info"
            disabled={loading}
            sx={{ color: "white" }}
          >
            {loading ? "Registrando..." : "Registrar Movimiento"}
          </Button>
        </DialogActions>
      </Dialog>
    </MDBox>
  );
}
